import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParentShell } from './ParentShell';
import { ApiError, api } from '@/lib/api';

/**
 * «Подробнее по предмету»: таблица предметы × (Сред./Итог по каждому
 * триместру + Сред./Итог за год). Идёт из `/api/parent/journal`
 * ссылкой «Подробнее по предмету →».
 *
 * Для безотметочных классов (0-2, детсад) рядов нет — бэк отдаёт
 * `class.hasGrades=false` и пустой `rows`. Для детсада вообще не
 * долетим: редирект в ParentShell отправит на «Главную».
 */
export default function ParentGradesPage() {
  const { child, activeYear } = useParentShell();

  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!child || !activeYear) return;
    let cancelled = false;
    setLoad(true); setError(null);
    api.get(`/api/parent/grades?student_id=${child.id}&year_id=${activeYear.id}`)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoad(false); });
    return () => { cancelled = true; };
  }, [child?.id, activeYear?.id]);

  if (loading) {
    return <div className="pc-body" style={{ color: 'var(--pc-fg-faint)' }}>Загружаем оценки…</div>;
  }
  if (error) {
    return <div className="pc-body" style={{ color: 'var(--pc-warn)' }}>Ошибка: {error}</div>;
  }
  if (!data) return null;

  const { rows, trimesters, class: cls } = data;
  const hasGrades = !!cls?.hasGrades;

  return (
    <div className="pc-body">
      <div className="pc-card">
        <div className="pc-card__head">
          <Link to="/parent/journal" className="pc-card__action" style={{ marginLeft: 0, marginRight: 8 }}>
            ← Дневник
          </Link>
          <div className="pc-card__title">Оценки по предметам</div>
          <span className="pc-card__sub">{cls?.label || ''}</span>
        </div>
        <div className="pc-card__body">
          {!hasGrades && (
            <div style={{ color: 'var(--pc-fg-faint)', padding: '12px 0' }}>
              У этого класса безотметочное обучение — оценок нет.
            </div>
          )}
          {hasGrades && (!trimesters || trimesters.length === 0) && (
            <div style={{ color: 'var(--pc-fg-faint)', padding: '12px 0' }}>
              Триместры для учебного года ещё не настроены.
            </div>
          )}
          {hasGrades && trimesters && trimesters.length > 0 && (
            <div className="pc-grades-wrap">
              <table className="pc-grades">
                <thead>
                  <tr>
                    <th className="pc-grades__subj" rowSpan={2}>Предмет</th>
                    {trimesters.map((t) => (
                      <th key={t.index} colSpan={2} className="pc-grades__group">
                        Триместр {t.label}
                      </th>
                    ))}
                    <th colSpan={2} className="pc-grades__group pc-grades__group--year">Год</th>
                  </tr>
                  <tr>
                    {trimesters.map((t) => (
                      <React.Fragment key={t.index}>
                        <th className="pc-grades__small">Сред.</th>
                        <th className="pc-grades__small">Итог</th>
                      </React.Fragment>
                    ))}
                    <th className="pc-grades__small">Сред.</th>
                    <th className="pc-grades__small">Итог</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.subjectId}>
                      <td className="pc-grades__subj">{r.subjectName}</td>
                      {r.tri.map((cell, i) => (
                        <React.Fragment key={i}>
                          <td className="pc-grades__cell">
                            {cell.avg != null
                              ? <span className="pc-grades__avg">{Number(cell.avg).toFixed(1)}</span>
                              : <span className="pc-grades__empty">—</span>}
                          </td>
                          <td className="pc-grades__cell">
                            {cell.final
                              ? <span className="pc-grades__final">{cell.final}</span>
                              : <span className="pc-grades__empty"></span>}
                          </td>
                        </React.Fragment>
                      ))}
                      <td className="pc-grades__cell pc-grades__cell--year">
                        {r.year.avg != null
                          ? <span className="pc-grades__avg">{Number(r.year.avg).toFixed(1)}</span>
                          : <span className="pc-grades__empty">—</span>}
                      </td>
                      <td className="pc-grades__cell pc-grades__cell--year">
                        {r.year.final
                          ? <span className="pc-grades__final">{r.year.final}</span>
                          : <span className="pc-grades__empty"></span>}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={trimesters.length * 2 + 3}
                          style={{ color: 'var(--pc-fg-faint)', padding: '12px 0', textAlign: 'center' }}>
                        В этом классе пока нет предметов в расписании.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
